/**
 * @fileoverview Is this hostname a real, publicly-trackable origin?
 * @module lib/features/core/trackableHost
 *
 * Ported into the SDK itself so `<FlowgridProvider>` gets it for free — every
 * npm consumer used to have to write this. The bug it exists to prevent: a
 * developer running an app on localhost and signing in was identifying to
 * PRODUCTION Flowgrid, creating an identified, zero-visit visitor row that
 * looked exactly like a real one. `init()`'s own auto-instrumentation guard
 * covers page/visit capture, but the identify path (`setUser`) bypassed it —
 * nothing from a dev machine should ever reach production without saying so.
 */

/**
 * True only for a real, publicly-trackable origin. False for localhost,
 * loopback, `*.local`, and RFC-1918 private-LAN IPs — every shape a
 * developer's machine takes. Pass a bare hostname (no port); use
 * `new URL(x).hostname` or `window.location.hostname`.
 */
export function isTrackableHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  const h = hostname.trim().toLowerCase().replace(/^\[|\]$/g, "");
  if (!h) return false;
  if (h === "localhost" || h.endsWith(".localhost")) return false;
  if (h.endsWith(".local")) return false;
  if (h === "::1" || h === "0.0.0.0") return false;
  if (/^127\./.test(h)) return false; // IPv4 loopback
  if (/^10\./.test(h)) return false; // RFC-1918
  if (/^192\.168\./.test(h)) return false; // RFC-1918
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(h)) return false; // RFC-1918
  if (/^169\.254\./.test(h)) return false; // link-local
  return true;
}
